import React, { useMemo } from 'react';
import { MenuItem, ListItemIcon, ListItemText, Typography, Grid, Chip, useTheme, Box, Divider, List, ListItem } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoIcon from '@mui/icons-material/Info';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SchoolIcon from '@mui/icons-material/School';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FactCheckIcon from '@mui/icons-material/FactCheck';

import MuiDialog from '../../SystemDesign/MuiDialog';
import { tokens } from '../../theme';
import { formattedDate, isEmpty } from '../../Utils/Functions';
import { DialogSection, DialogSectionLabel, DialogSectionValue } from '../../SystemDesign/Styles';
import { useGetUserQuery } from '../../services/SkillsManagement';

/**
 * SeeMoreEvaluation - Detailed view of a single evaluation record
 * Props: { row: { original: evaluationLike } }
 */
const SeeMoreEvaluation = ({ row }) => {
  const evaluation = row?.original || {};
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const employeeId = evaluation?.employee_id || evaluation?.employeeId || evaluation?.employee?.id;
  const { data: userResp } = useGetUserQuery(
    employeeId ? { userId: String(employeeId) } : { userId: '' },
    
  );

  // Resolve fields defensively from multiple shapes
  const data = useMemo(() => {
    const id = evaluation?.evaluation_id ?? evaluation?.id ?? evaluation?.evaluationId;
    const score = evaluation?.score;
    const comments = evaluation?.comments;
    const level = evaluation?.level;
    const evaluationType = evaluation?.evaluation_type ?? evaluation?.evaluationType;
    const evaluationDate = evaluation?.evaluation_date ?? evaluation?.evaluationDate;
    const skillName = evaluation?.skill?.name ?? evaluation?.skillName;
    const skillCode = evaluation?.skill?.skill_code ?? evaluation?.skillCode;
    const discipline = evaluation?.skill?.discipline ?? evaluation?.discipline;
    // Resolve employee name with API fallback
    const apiUser = userResp?.user;
    const fn = apiUser?.profile?.firstName || evaluation?.employee?.profile?.firstName || evaluation?.firstName;
    const ln = apiUser?.profile?.lastName || evaluation?.employee?.profile?.lastName || evaluation?.lastName;
    const employeeName = (fn || ln)
      ? `${fn || ''} ${ln || ''}`.trim()
      : (evaluation?.employee?.username || evaluation?.employeeName || evaluation?.employee?.email);
    const posteName = evaluation?.poste?.name ?? evaluation?.posteName ?? apiUser?.Poste?.name ?? apiUser?.poste?.name;
    const evidences = Array.isArray(evaluation?.evidences) ? evaluation.evidences : [];
    const createdAt = evaluation?.createdAt;
    const updatedAt = evaluation?.updatedAt;

    // Required fields: evaluation -> user.Poste.skillRequirements (match skill_id)
    let requiredMin = evaluation?.required_min_score ?? evaluation?.min_score ?? null;
    let requiredMax = evaluation?.required_max_score ?? evaluation?.max_score ?? null;
    let requiredLevel = evaluation?.required_level ?? evaluation?.requiredLevel ?? null;
    if ((requiredMin == null || requiredMax == null || !requiredLevel) && (apiUser?.Poste?.skillRequirements || apiUser?.poste?.skillRequirements)) {
      const sId = evaluation?.skill_id || evaluation?.skill?.skill_id || evaluation?.skill?.id;
      const reqList = apiUser?.Poste?.skillRequirements || apiUser?.poste?.skillRequirements || [];
      const match = Array.isArray(reqList) ? reqList.find(r => Number(r?.skill_id ?? r?.skill?.id) === Number(sId)) : null;
      if (match) {
        if (requiredMin == null) requiredMin = match?.min_score ?? null;
        if (requiredMax == null) requiredMax = match?.max_score ?? null;
        if (!requiredLevel) requiredLevel = match?.required_level ?? null;
      }
    }

    return { id, score, comments, level, evaluationType, evaluationDate, skillName, skillCode, discipline, employeeName, posteName, evidences, createdAt, updatedAt, requiredMin, requiredMax, requiredLevel };
  }, [evaluation, userResp]);

  if (!data.id) return null;

  const levelText = (n) => {
    if (n === 'awareness' || n === 1) return 'Awareness (1)';
    if (n === 'skilled_application' || n === 2) return 'Skilled Application (2)';
    if (n === 'management' || n === 3) return 'Management (3)';
    return isEmpty(n) ? '-' : String(n);
  };

  const DetailItem = ({ label, value, chip, custom, icon }) => (
    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {label && (
        <Typography sx={DialogSectionLabel(colors)}>
          {icon && React.cloneElement(icon, { fontSize: 'small' })}
          {label}:
        </Typography>
      )}
      {chip ? (
        <Box sx={{ '& .MuiChip-root': { transition: 'all 0.2s', '&:hover': { opacity: 0.8 } } }}>
          {chip}
        </Box>
      ) : custom ? (
        custom
      ) : (
        <Typography sx={DialogSectionValue(colors)}>{isEmpty(value) ? '-' : value}</Typography>
      )}
    </Box>
  );

  const SectionHeader = ({ title, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 2 }}>
      <Typography sx={{ ...DialogSection(colors), mr: 1 }}>
        {icon}{title}
      </Typography>
      <Divider sx={{ flexGrow: 1 }} />
    </Box>
  );

  const sections = [
    {
      id: 'basic',
      title: 'Basic Information',
      icon: <PersonOutlineIcon sx={{ mr: 1, fontSize: '1.2rem' }} />,
      content: [
        { label: 'Employee', value: data.employeeName },
        { label: 'Position', value: data.posteName },
        { label: 'Evaluation Type', value: data.evaluationType },
        { label: 'Evaluation Date', value: data.evaluationDate ? formattedDate(new Date(data.evaluationDate)) : '-' },
      ],
    },
    {
      id: 'skill',
      title: 'Skill Details',
      icon: <SchoolIcon sx={{ mr: 1, fontSize: '1.2rem' }} />,
      content: [
        {
          grid: [
            { label: 'Skill Name', value: data.skillName },
            { label: 'Skill Code', value: data.skillCode, chip: data.skillCode ? <Chip size="small" variant="outlined" label={data.skillCode} /> : null },
            { label: 'Required Score', value: (Number.isFinite(Number(data.requiredMin)) && Number.isFinite(Number(data.requiredMax))) ? `${Number(data.requiredMin)}-${Number(data.requiredMax)}/10` : '-' },
            { label: 'Required Level', value: data.requiredLevel ? levelText(data.requiredLevel) : '-' },
          ],
        },
      ],
    },
    {
      id: 'results',
      title: 'Results',
      icon: <AssessmentIcon sx={{ mr: 1, fontSize: '1.2rem' }} />,
      content: [
        {
          grid: [
            { label: 'Score', value: isEmpty(data.score) ? '-' : `${data.score}/10` },
            { label: 'Level', value: levelText(data.level) },
            { label: 'Comments', value: data.comments },
          ],
        },
      ],
    },
    {
      id: 'evidences',
      title: 'Practical Evidences',
      icon: <FactCheckIcon sx={{ mr: 1, fontSize: '1.2rem' }} />,
      content: [
        {
          label: '',
          value: '',
          custom: (
            Array.isArray(data.evidences) && data.evidences.length > 0 ? (
              <List dense>
                {data.evidences.map((ev, idx) => (
                  <ListItem key={ev?.evidence_id ?? ev?.id ?? idx} sx={{ pl: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <Typography variant="body2">
                        {ev?.evidence_type || ev?.type || 'Evidence'}
                        {ev?.evidence_url && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {ev.evidence_url}
                          </Typography>
                        )}
                      </Typography>
                      {ev?.status && (
                        <Chip size="small" variant="outlined" label={String(ev.status)} />
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">No evidences for this evaluation.</Typography>
            )
          ),
        },
      ],
    },
  ];

  const dialogContent = (
    <Grid container direction="column" spacing={1}>
      {sections.map((section) => (
        <Grid item size={{xs:12}} key={section.id}>
          <SectionHeader title={section.title} icon={section.icon} />
          {section.content.map((item, index) => (
            <React.Fragment key={index}>
              {item.grid ? (
                <Grid container direction="column" spacing={2}>
                  {item.grid.map((gridItem, gridIndex) => (
                    <Grid item size={{xs:12}} key={gridIndex}>
                      <DetailItem {...gridItem} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <DetailItem {...item} />
              )}
            </React.Fragment>
          ))}
        </Grid>
      ))}
    </Grid>
  );

  const triggerButton = (
    <MenuItem>
      <ListItemIcon>
        <VisibilityIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>See More</ListItemText>
    </MenuItem>
  );

  return (
    <MuiDialog
      icon={<InfoIcon />}
      triggerButton={triggerButton}
      title="Evaluation Information"
      subtitle={`Detailed information for evaluation for ${data.skillName || 'Skill'} of employee ${data.employeeName || '-'}`}
      content={dialogContent}
      existAction={false}
      maxWidth="md"
    />
  );
};

export default SeeMoreEvaluation;

